import { useState } from 'react';
import authenticateTH from '../../api/authenticateTH';
import convertToCoachAccount from '../../api/convertToCoachAccount';
import useAuthentication from './useAuthentication';
import useLoadingDialog from './useLoadingDialog';
import SegmentService from '../../../lib/segment/SegmentService';
import { SEGMENT_EVENTS } from '../../../lib/segment/SegmentEvents';
import { getConsentFlags } from '../../../lib/consent/oneTrustConsent';

type ConversionPayload = {
    accountCreationLocation?: string;
};

export default function useCoachConversion() {
    const [convertingInProcess, setConvertingInProcess] = useState(false);
    const [convertingComplete, setConvertingComplete] = useState<boolean | null>(null);
    const [serverError, setServerError] = useState(false);
    const [loginErrorMessage, setLoginErrorMessage] = useState('');

    const { loginUser } = useAuthentication();
    const { isOpen, progress, startProgress, stopProgress } = useLoadingDialog();

    const trackConversionSuccess = (userId: number, email: string, payload: ConversionPayload) => {
        const { performanceAllowed, targetingAllowed } = getConsentFlags(window.OnetrustActiveGroups);

        if (performanceAllowed) {
            SegmentService.identify(userId, email);
            SegmentService.track(SEGMENT_EVENTS.CreateCoachAccount.key, {
                formName: 'Coach Account Signup',
                userType: 'Coach',
                formData: {
                    email
                }
            });
        }

        if (targetingAllowed) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'coach_trial_created',
                coach_id: userId,
                plan_type: 'trial',
                signup_source: payload.accountCreationLocation || 'account',
                referrer: document.referrer || '',
                utm_source: SegmentService.params?.utm_source || '',
                utm_medium: SegmentService.params?.utm_medium || '',
                utm_campaign: SegmentService.params?.utm_campaign || '',
                utm_term: SegmentService.params?.utm_term || '',
                utm_content: SegmentService.params?.utm_content || ''
            });
        }
    };

    const convertExistingAccount = async (email: string, password: string, payload: ConversionPayload) => {
        setConvertingComplete(null);
        setLoginErrorMessage('');
        setServerError(false);
        setConvertingInProcess(true);
        startProgress();

        const authResponse = await authenticateTH(email, password);
        if (!authResponse?.session_id) {
            setLoginErrorMessage('Incorrect email or password');
            setConvertingComplete(false);
            setConvertingInProcess(false);
            stopProgress();
            return { loginFailed: true };
        }

        const response = await convertToCoachAccount(payload, authResponse.session_id);

        if (response?.code === 'ALREADY_COACH') {
            await loginUser(authResponse.session_id);
            setConvertingComplete(true);
            setConvertingInProcess(false);
            stopProgress();
            return { alreadyCoach: true };
        }

        if (response?.session_id) {
            await loginUser(response.session_id);
            trackConversionSuccess(response.id, email, payload);
            setConvertingComplete(true);
            setConvertingInProcess(false);
            stopProgress();
            return { success: true, response };
        }

        setServerError(true);
        setConvertingComplete(false);
        setConvertingInProcess(false);
        stopProgress();
        return { error: true };
    };

    const isInProcess = convertingComplete || convertingInProcess;

    return {
        convertExistingAccount,
        convertingInProcess,
        convertingComplete,
        isInProcess,
        isOpen,
        progress,
        serverError,
        setServerError,
        loginErrorMessage,
        setLoginErrorMessage
    };
}
